import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { fetchAllClients, fetchAllPrograms } from './airtable.js';

/**
 * Security utilities for input sanitization
 */

/**
 * Sanitizes a string to prevent XSS attacks while preserving data integrity
 * @param {any} value - Value to sanitize
 * @param {number} maxLength - Maximum length allowed
 * @returns {string} Sanitized string
 */
function sanitizeString(value, maxLength = 10000) {
  if (value === null || value === undefined) {
    return null;
  }
  
  let str = String(value);
  
  // Limit length to prevent excessively large payloads
  if (str.length > maxLength) {
    str = str.substring(0, maxLength);
  }
  
  // Remove potentially dangerous HTML tags and attributes
  str = str
    // Remove script tags and their content (case insensitive)
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    // Remove javascript: protocol links
    .replace(/javascript:/gi, '')
    // Remove on* event handlers (onclick, onload, etc.)
    .replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '')
    // Remove other potentially dangerous tags
    .replace(/<(iframe|object|embed|form|input|meta|link)[^>]*>/gi, '')
    // Escape remaining HTML tags to prevent injection
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Escape quotes to prevent attribute injection
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    // Remove null bytes and other control characters
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, ''); // eslint-disable-line no-control-regex
  
  return str.trim();
}

/**
 * Recursively sanitizes an object or array of survey responses
 * @param {any} obj - Object to sanitize
 * @param {number} maxDepth - Maximum recursion depth to prevent stack overflow
 * @returns {any} Sanitized object
 */
export function sanitizeResponseData(obj, maxDepth = 10) {
  if (maxDepth <= 0) {
    return '[MAX_DEPTH_EXCEEDED]';
  }
  
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (typeof obj === 'string') {
    return sanitizeString(obj);
  }
  
  if (typeof obj === 'number' || typeof obj === 'boolean') {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeResponseData(item, maxDepth - 1));
  }
  
  if (typeof obj === 'object') {
    const sanitized = {};
    for (const [key, value] of Object.entries(obj)) {
      // Sanitize the key as well
      const sanitizedKey = sanitizeString(key, 500);
      if (sanitizedKey) {
        sanitized[sanitizedKey] = sanitizeResponseData(value, maxDepth - 1);
      }
    }
    return sanitized;
  }
  
  // For any other type, convert to string and sanitize
  return sanitizeString(String(obj));
}

/**
 * Client-side survey import utilities for parsing Excel/CSV files
 * Transforms external survey data into the External Survey JSON format
 */

// Supported file types
export const SUPPORTED_FILE_TYPES = {
  EXCEL: ['xlsx', 'xls'],
  CSV: ['csv'],
  ALL: ['xlsx', 'xls', 'csv']
};

// Question type detection patterns
const QUESTION_TYPE_PATTERNS = {
  NPS: {
    keywords: ['recommend', 'likely', 'nps', 'net promoter', 'referral', 'word of mouth'],
    valueRange: { min: 0, max: 10 }
  },
  RATING: {
    keywords: ['rate', 'rating', 'scale', 'how would you', 'effectiveness', 'satisfaction', 'quality'],
    valueRange: { min: 1, max: 10 }
  },
  YES_NO: {
    keywords: ['would you', 'do you', 'did you', 'will you', 'have you'],
    values: ['yes', 'no', 'true', 'false', 'y', 'n']
  },
  EMAIL: {
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  },
  DATE: {
    patterns: [
      /^\d{4}-\d{2}-\d{2}$/,
      /^\d{2}\/\d{2}\/\d{4}$/,
      /^\d{2}-\d{2}-\d{4}$/
    ]
  },
  // MULTIPLE_SELECT: Removed to prevent false positives with text containing commas
};

/**
 * Parse file content based on file type
 * @param {File} file - The uploaded file
 * @returns {Promise<{data: Array, metadata: Object}>}
 */
export async function parseFile(file) {
  const fileExtension = getFileExtension(file.name);
  
  if (!SUPPORTED_FILE_TYPES.ALL.includes(fileExtension)) {
    throw new Error(`Unsupported file type: ${fileExtension}. Supported types: ${SUPPORTED_FILE_TYPES.ALL.join(', ')}`);
  }

  const fileSize = file.size;
  const maxSize = 10 * 1024 * 1024; // 10MB limit
  
  if (fileSize > maxSize) {
    throw new Error(`File size (${Math.round(fileSize / 1024 / 1024)}MB) exceeds limit of 10MB`);
  }

  try {
    if (SUPPORTED_FILE_TYPES.EXCEL.includes(fileExtension)) {
      return await parseExcelFile(file);
    } else if (SUPPORTED_FILE_TYPES.CSV.includes(fileExtension)) {
      return await parseCSVFile(file);
    }
  } catch (error) {
    throw new Error(`Failed to parse ${fileExtension.toUpperCase()} file: ${error.message}`);
  }
}

/**
 * Parse Excel file using XLSX library
 * @param {File} file - Excel file
 * @returns {Promise<{data: Array, metadata: Object}>}
 */
async function parseExcelFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Use first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to JSON with headers, preserving empty cells
        const rawJsonData = XLSX.utils.sheet_to_json(worksheet, { 
          header: 1,
          defval: null, // Use null for empty cells instead of omitting them
          raw: false    // Ensure consistent string handling
        });
        
        if (rawJsonData.length === 0) {
          throw new Error('Excel file is empty or contains no data');
        }

        // Normalize all rows to have the same length as headers
        const headerLength = rawJsonData[0]?.length || 0;
        const jsonData = rawJsonData.map((row, rowIndex) => {
          // Create normalized array filled with nulls
          const normalizedRow = new Array(headerLength).fill(null);
          
          // Copy existing values into normalized array
          row.forEach((value, colIndex) => {
            normalizedRow[colIndex] = value;
          });
          
          return normalizedRow;
        });

        // Excel file successfully parsed

        const metadata = {
          file_format: getFileExtension(file.name),
          header_row: 1,
          data_start_row: 2,
          total_rows: jsonData.length,
          total_columns: jsonData[0]?.length || 0,
          sheet_name: sheetName,
          encoding: 'utf-8'
        };

        resolve({
          data: jsonData,
          metadata
        });
      } catch (error) {
        reject(new Error(`Excel parsing error: ${error.message}`));
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read Excel file'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Parse CSV file using PapaParse
 * @param {File} file - CSV file
 * @returns {Promise<{data: Array, metadata: Object}>}
 */
async function parseCSVFile(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      complete: (results) => {
        try {
          if (results.errors.length > 0) {
            // CSV parsing warnings will be included in metadata
            metadata.parsing_warnings = results.errors.map(error => error.message || error);
          }
          
          if (!results.data || results.data.length === 0) {
            throw new Error('CSV file is empty or contains no data');
          }

          const metadata = {
            file_format: 'csv',
            header_row: 1,
            data_start_row: 2,
            total_rows: results.data.length,
            total_columns: results.data[0]?.length || 0,
            delimiter: results.meta.delimiter,
            encoding: 'utf-8'
          };

          resolve({
            data: results.data,
            metadata
          });
        } catch (error) {
          reject(new Error(`CSV parsing error: ${error.message}`));
        }
      },
      error: (error) => {
        reject(new Error(`CSV parsing failed: ${error.message}`));
      },
      header: false,
      skipEmptyLines: true,
      encoding: 'UTF-8'
    });
  });
}

/**
 * Analyze parsed data and detect question structure
 * @param {Array} data - Parsed file data (2D array)
 * @param {Object} metadata - File metadata
 * @returns {Object} Question mapping structure
 */
export function analyzeQuestionStructure(data, metadata) {
  if (!data || data.length < 2) {
    throw new Error('Insufficient data to analyze question structure');
  }

  const headers = data[0];
  const sampleRows = data.slice(1, Math.min(6, data.length)); // Analyze first 5 data rows
  
  const questions = headers.map((header, index) => {
    if (!header || header.trim() === '') {
      return null; // Skip empty headers
    }

    const columnLetter = getExcelColumnLetter(index);
    const sampleValues = sampleRows
      .map(row => row[index])
      .filter(value => value !== null && value !== undefined && value !== '');

    const questionType = detectQuestionType(header, sampleValues);
    
    return {
      id: `q${index + 1}`,
      title: header.trim(),
      type: questionType.type,
      column: columnLetter,
      columnIndex: index, // Preserve original column index
      required: false,
      ...questionType.additionalProps
    };
  }).filter(q => q !== null);

  return {
    questions,
    metadata: {
      ...metadata,
      questions_detected: questions.length,
      analysis_date: new Date().toISOString()
    }
  };
}

/**
 * Detect question type based on header text and sample values
 * @param {string} header - Question header text
 * @param {Array} sampleValues - Sample values from the column
 * @returns {Object} Question type information
 */
function detectQuestionType(header, sampleValues) {
  // Simplified: All questions are treated as long_text to avoid classification issues
  // External survey data can contain any type of response and long_text handles all cases
  return { type: 'long_text' };
}

/**
 * Resolve client and program names to Airtable record IDs
 * @param {string} clientName - Client name to lookup
 * @param {string} programName - Program name to lookup
 * @returns {Object} Object with clientId and programId
 */
export async function resolveClientAndProgramIds(clientName, programName) {
  try {
    // Fetch all clients and programs
    const [clients, programs] = await Promise.all([
      fetchAllClients(),
      fetchAllPrograms()
    ]);

    // Find matching client
    const client = clients.find(c => c.name === clientName);
    if (!client) {
      throw new Error(`Client not found: ${clientName}. Available clients: ${clients.map(c => c.name).join(', ')}`);
    }

    // Find matching program
    const program = programs.find(p => p.name === programName);
    if (!program) {
      throw new Error(`Program not found: ${programName}. Available programs: ${programs.map(p => p.name).join(', ')}`);
    }

    return {
      clientId: client.id,
      programId: program.id
    };
  } catch (error) {
    throw new Error(`Failed to resolve client/program IDs: ${error.message}`);
  }
}

/**
 * Transform parsed data into External Survey JSON format
 * @param {Array} data - Parsed file data
 * @param {Object} questionMapping - Question mapping structure
 * @param {Object} importMetadata - Import metadata (client, program, etc.)
 * @returns {Object} Transformed survey data
 */
export function transformToSurveyFormat(data, questionMapping, importMetadata) {
  if (!data || data.length < 2) {
    throw new Error('Insufficient data to transform');
  }

  const headers = data[0];
  const allDataRows = data.slice(1);
  
  // Filter out empty rows (rows where all cells are empty/null/undefined)
  const dataRows = allDataRows.filter(row => {
    return row && row.some(cell => cell !== null && cell !== undefined && String(cell).trim() !== '');
  });
  
  // Create question mapping by original column index
  const questionsByColumn = {};
  questionMapping.questions.forEach((question) => {
    questionsByColumn[question.columnIndex] = question;
  });

  // Transform each data row into a response
  const responses = dataRows.map((row, rowIndex) => {
    const responseData = {
      responses: {},
      metadata: {
        import_date: new Date().toISOString(),
        original_row: rowIndex + 2, // +2 because we skip header and use 1-based indexing
        data_quality: 'complete',
        validation_warnings: []
      }
    };

    // Process row data

    // Process all expected columns (not just the ones present in this row)
    const maxColumnIndex = Math.max(...Object.keys(questionsByColumn).map(Number));
    
    for (let colIndex = 0; colIndex <= maxColumnIndex; colIndex++) {
      const question = questionsByColumn[colIndex];
      if (!question) {
        continue;
      }

      const cellValue = row[colIndex]; // This will be undefined for missing trailing cells
      const processedValue = processAnswer(cellValue, question, responseData.metadata.validation_warnings);
      
      responseData.responses[question.id] = {
        question: question.title,
        answer: processedValue.answer,
        type: question.type,
        raw_value: String(cellValue || '')
      };
    }

    // Calculate data quality
    const totalQuestions = questionMapping.questions.length; // Use total questions in survey, not just processed
    const completedQuestions = Object.values(responseData.responses)
      .filter(response => response.answer !== null && response.answer !== '').length;
    
    const qualityPercentage = totalQuestions > 0 ? (completedQuestions / totalQuestions) * 100 : 0;
    
    if (qualityPercentage >= 90) {
      responseData.metadata.data_quality = 'complete';
    } else if (qualityPercentage >= 70) {
      responseData.metadata.data_quality = 'partial';
    } else {
      responseData.metadata.data_quality = 'invalid';
    }

    return responseData;
  });

  return {
    importRecord: {
      'Client': importMetadata.client,
      'Program': importMetadata.program,
      'Import Date': new Date().toISOString().split('T')[0],
      'Source Type': importMetadata.sourceType || 'Client Excel',
      'Survey Title': importMetadata.surveyTitle || 'External Survey',
      'Original Filename': importMetadata.originalFilename,
      'Response Count': responses.length,
      'Question Mapping': JSON.stringify(questionMapping),
      'Import Notes': importMetadata.notes || '',
      'Imported By': importMetadata.importedBy || 'Admin'
    },
    associatedSOs: importMetadata.associatedSOs, // Store separately for SO associations table
    responses: responses,
    summary: {
      total_responses: responses.length,
      complete_responses: responses.filter(r => r.metadata.data_quality === 'complete').length,
      partial_responses: responses.filter(r => r.metadata.data_quality === 'partial').length,
      invalid_responses: responses.filter(r => r.metadata.data_quality === 'invalid').length,
      questions_count: questionMapping.questions.length
    }
  };
}

/**
 * Process individual answer based on question type
 * @param {any} rawValue - Raw cell value
 * @param {Object} question - Question definition
 * @param {Array} warnings - Array to collect validation warnings
 * @returns {Object} Processed answer
 */
function processAnswer(rawValue, question, warnings) {
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return { answer: null };
  }

  const stringValue = String(rawValue).trim();

  try {
    switch (question.type) {
      case 'number': {
        const numValue = parseFloat(stringValue);
        if (isNaN(numValue)) {
          warnings.push(`Invalid number format: "${stringValue}"`);
          return { answer: null };
        }
        return { answer: numValue };
      }

      case 'rating':
      case 'nps': {
        const ratingValue = parseFloat(stringValue.split(' ')[0]); // Extract number from "4 - Very Good"
        if (isNaN(ratingValue)) {
          warnings.push(`Invalid ${question.type} value: "${stringValue}"`);
          return { answer: null };
        }
        
        const scale = question.scale;
        if (scale && (ratingValue < scale.min || ratingValue > scale.max)) {
          warnings.push(`${question.type} value ${ratingValue} outside range ${scale.min}-${scale.max}`);
          return { answer: Math.max(scale.min, Math.min(scale.max, ratingValue)) };
        }
        
        return { answer: ratingValue };
      }

      case 'yes_no': {
        const lowerValue = stringValue.toLowerCase();
        if (['yes', 'y', 'true', '1'].includes(lowerValue)) {
          return { answer: true };
        } else if (['no', 'n', 'false', '0'].includes(lowerValue)) {
          return { answer: false };
        } else {
          warnings.push(`Ambiguous yes/no value: "${stringValue}"`);
          return { answer: null };
        }
      }

      case 'multiple_select': {
        const options = stringValue.split(/[,;|]|(?:\s+and\s+)|(?:\s+&\s+)/)
          .map(opt => sanitizeString(opt.trim()))
          .filter(opt => opt && opt.length > 0);
        return { answer: options };
      }

      case 'email': {
        const sanitizedEmail = sanitizeString(stringValue);
        if (!QUESTION_TYPE_PATTERNS.EMAIL.pattern.test(sanitizedEmail)) {
          warnings.push(`Invalid email format: "${sanitizedEmail}"`);
          return { answer: sanitizedEmail }; // Keep sanitized version for manual review
        }
        return { answer: sanitizedEmail };
      }

      case 'date': {
        // Attempt to parse date
        const dateValue = new Date(stringValue);
        if (isNaN(dateValue.getTime())) {
          warnings.push(`Invalid date format: "${stringValue}"`);
          return { answer: stringValue };
        }
        return { answer: dateValue.toISOString().split('T')[0] };
      }

      case 'text':
      case 'long_text':
      default: {
        // Sanitize text responses to prevent XSS
        const sanitizedText = sanitizeString(stringValue);
        return { answer: sanitizedText };
      }
    }
  } catch (error) {
    warnings.push(`Processing error for "${stringValue}": ${error.message}`);
    return { answer: stringValue };
  }
}

/**
 * Validate import data against requirements
 * @param {Object} surveyData - Transformed survey data
 * @param {Object} importData - Import data containing client, program, associatedSOs
 * @returns {Object} Validation results
 */
export function validateImportData(surveyData, importData) {
  const errors = [];
  const warnings = [];

  // Validate SO number(s) - handle comma-separated values
  const associatedSOs = importData.associatedSOs;
  if (!associatedSOs || associatedSOs.trim() === '') {
    errors.push('Valid SO number is required');
  } else {
    // Split by comma and validate each SO number
    const soNumbers = associatedSOs.split(',').map(so => so.trim());
    const invalidSOs = soNumbers.filter(so => !so || isNaN(parseInt(so)));
    
    if (invalidSOs.length > 0) {
      errors.push(`Invalid SO numbers: ${invalidSOs.join(', ')}. SO numbers must be numeric.`);
    }
  }

  // Validate response count
  if (!surveyData.responses || surveyData.responses.length === 0) {
    errors.push('No valid responses found in file');
  } else if (surveyData.responses.length > 1000) {
    warnings.push(`Large number of responses (${surveyData.responses.length}). Consider splitting into smaller batches.`);
  }

  // Validate question count
  const questionCount = surveyData.summary?.questions_count || 0;
  if (questionCount === 0) {
    errors.push('No valid questions detected in file');
  } else if (questionCount > 100) {
    warnings.push(`Large number of questions (${questionCount}). This may impact performance.`);
  }

  // Validate data quality
  const invalidResponses = surveyData.summary?.invalid_responses || 0;
  const totalResponses = surveyData.summary?.total_responses || 0;
  
  if (invalidResponses > 0) {
    const invalidPercentage = (invalidResponses / totalResponses) * 100;
    if (invalidPercentage > 50) {
      errors.push(`High percentage of invalid responses (${invalidPercentage.toFixed(1)}%)`);
    } else if (invalidPercentage > 20) {
      warnings.push(`Some invalid responses detected (${invalidPercentage.toFixed(1)}%)`);
    }
  }

  // Check for duplicate detection
  const duplicateWarning = checkForPotentialDuplicates(surveyData, importData.associatedSOs);
  if (duplicateWarning) {
    warnings.push(duplicateWarning);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    summary: {
      total_responses: totalResponses,
      valid_responses: totalResponses - invalidResponses,
      invalid_responses: invalidResponses,
      questions_detected: questionCount
    }
  };
}

/**
 * Generate preview data for UI display
 * @param {Object} surveyData - Transformed survey data
 * @param {number} maxRows - Maximum rows to include in preview
 * @returns {Object} Preview data structure
 */
export function generatePreviewData(surveyData, maxRows = 5) {
  const { importRecord, responses, summary, questionMapping: directMapping } = surveyData;
  
  // Try to get question mapping from direct property first, then from importRecord
  let questionMapping;
  if (directMapping) {
    questionMapping = directMapping;
  } else if (importRecord['Question Mapping']) {
    questionMapping = JSON.parse(importRecord['Question Mapping']);
  } else {
    throw new Error('No question mapping available for preview generation');
  }
  
  const previewResponses = responses.slice(0, maxRows);

  return {
    import: {
      so_number: importRecord.SO,
      survey_title: importRecord['Survey Title'],
      source_type: importRecord['Source Type'],
      filename: importRecord['Original Filename'],
      response_count: importRecord['Response Count']
    },
    questions: questionMapping.questions.map(q => ({
      id: q.id,
      title: q.title,
      type: q.type,
      column: q.column
    })),
    preview_responses: previewResponses.map((response, index) => ({
      row_number: index + 1,
      data_quality: response.metadata.data_quality,
      sample_answers: Object.entries(response.responses)
        .slice(0, 3) // Show first 3 questions
        .map(([questionId, data]) => ({
          question: data.question,
          answer: data.answer,
          type: data.type
        }))
    })),
    responses: previewResponses, // Add responses array for table preview
    summary: {
      ...summary,
      data_quality_distribution: {
        complete: summary.complete_responses,
        partial: summary.partial_responses,
        invalid: summary.invalid_responses
      }
    }
  };
}

// Helper functions

/**
 * Get file extension from filename
 * @param {string} filename - File name
 * @returns {string} File extension in lowercase
 */
function getFileExtension(filename) {
  return filename.split('.').pop().toLowerCase();
}

/**
 * Convert column index to Excel column letter
 * @param {number} index - Zero-based column index
 * @returns {string} Excel column letter (A, B, ..., Z, AA, AB, ...)
 */
function getExcelColumnLetter(index) {
  let result = '';
  while (index >= 0) {
    result = String.fromCharCode(65 + (index % 26)) + result;
    index = Math.floor(index / 26) - 1;
  }
  return result;
}

/**
 * Check for potential duplicate imports
 * @param {Object} surveyData - Survey data to check
 * @param {string} associatedSOs - Comma-separated SO numbers
 * @returns {string|null} Warning message if duplicates detected
 */
function checkForPotentialDuplicates(surveyData, associatedSOs) {
  // This would ideally check against existing imports in Airtable
  // For now, just warn about large imports that might be duplicates
  const responseCount = surveyData.summary?.total_responses || 0;
  
  if (responseCount > 100) {
    return `Large import detected (${responseCount} responses). Please verify this is not a duplicate import for SOs: ${associatedSOs}.`;
  }
  
  return null;
}

/**
 * Estimate memory usage for import
 * @param {Object} surveyData - Survey data
 * @returns {Object} Memory usage estimate
 */
export function estimateMemoryUsage(surveyData) {
  const jsonString = JSON.stringify(surveyData);
  const sizeInBytes = new Blob([jsonString]).size;
  const sizeInKB = Math.round(sizeInBytes / 1024);
  const sizeInMB = Math.round(sizeInKB / 1024);

  // Estimate Airtable field usage (100,000 character limit per Long Text field)
  const avgResponseSize = sizeInBytes / (surveyData.responses?.length || 1);
  const responsesPerField = Math.floor(100000 / avgResponseSize);

  return {
    total_size: {
      bytes: sizeInBytes,
      kb: sizeInKB,
      mb: sizeInMB
    },
    airtable_fields_needed: Math.ceil((surveyData.responses?.length || 0) / responsesPerField),
    responses_per_field: responsesPerField,
    recommendations: sizeInMB > 5 ? ['Consider splitting into smaller batches'] : []
  };
}